import is from '@sindresorhus/is';
import { z } from 'zod';

import {
    filterBooleanResponseSchema,
    filterDateResponseSchema,
    filterIntegerResponseSchema,
    filterJobResponseSchema,
    filterPercentResponseSchema,
    filterSearchResponseSchema,
    filterSkillResponseSchema,
    filterSkillTypeResponseSchema,
    filterStateResponseSchema,
    filterStringResponseSchema,
    filterUserResponseSchema,
    filterUuidResponseSchema,
    stringTypeLiteralSchema,
} from '~/shared/data-contract/filter-response';

export const filterBooleanResponseTransformedSchema = filterBooleanResponseSchema.transform((booleanResponse) => ({
    label: booleanResponse.field,
    ...booleanResponse,
}));

export const filterStringResponseTransformedSchema = filterStringResponseSchema.transform(({ values, ...rest }) => ({
    label: rest.field,
    options: is.array(values)
        ? values.filter(is.truthy).map((string) => ({
              id: string,
              label: string,
          }))
        : [],
    ...rest,
}));

export const filterStateResponseTransformedSchema = filterStateResponseSchema.transform((stateResponse) => ({
    label: stateResponse.field,
    ...stateResponse,
}));

export const filterSearchResponseTransformedSchema = filterSearchResponseSchema.transform((searchResponse) => ({
    label: searchResponse.field,
    ...searchResponse,
}));

export const filterIntegerResponseTransformedSchema = filterIntegerResponseSchema.transform(({ values, ...rest }) => ({
    label: rest.field,
    options: is.array(values)
        ? values.filter(is.number).map((number) => ({
              id: String(number),
              label: String(number),
          }))
        : [],
    ...rest,
}));

export const filterDateResponseTransformedSchema = filterDateResponseSchema.transform(({ ...rest }) => ({
    label: rest.field,
    ...rest,
}));

export const filterSkillResponseTransformedSchema = filterSkillResponseSchema.transform((skillResponse) => ({
    label: skillResponse.field,
    ...skillResponse,
}));

export const filterUuidResponseTransformedSchema = filterUuidResponseSchema.transform((uuidResponse) => ({
    label: uuidResponse.field,
    ...uuidResponse,
}));

export const filterUserResponseTransformedSchema = filterUserResponseSchema.transform(({ values, ...rest }) => ({
    label: rest.field,
    options: is.array(values)
        ? values.filter(is.truthy).map(({ id, firstName, lastName }) => ({
              id,
              label: `${firstName} ${lastName}`,
          }))
        : [],
    ...rest,
}));

export const filterJobResponseTransformedSchema = filterJobResponseSchema.transform((jobResponse) => ({
    label: jobResponse.field,
    ...jobResponse,
}));

export const filterPercentResponseTransformedSchema = filterPercentResponseSchema.transform((percentResponse) => ({
    label: percentResponse.field,
    ...percentResponse,
}));

export const filterSkillTypeResponseTransformedSchema = filterSkillTypeResponseSchema.transform(
    ({ values, ...rest }) => ({
        label: rest.field,
        options: values.map((id) => ({ id, label: id })),
        ...rest,
        type: stringTypeLiteralSchema.value,
    }),
);

const filterInFilterResponseTransformedSchema = z.union([
    filterBooleanResponseTransformedSchema,
    filterDateResponseTransformedSchema,
    filterIntegerResponseTransformedSchema,
    filterJobResponseTransformedSchema,
    filterPercentResponseTransformedSchema,
    filterSearchResponseTransformedSchema,
    filterSkillResponseTransformedSchema,
    filterSkillTypeResponseTransformedSchema,
    filterStateResponseTransformedSchema,
    filterStringResponseTransformedSchema,
    filterUserResponseTransformedSchema,
    filterUuidResponseTransformedSchema,
]);
export type FilterInFilterResponseTransformed = z.infer<typeof filterInFilterResponseTransformedSchema>;

export const filterResponseTransformedSchema = z
    .object({
        filters: filterInFilterResponseTransformedSchema.array(),
    })
    .transform(({ filters }) => filters);
